import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { LuEye, LuEyeOff } from "react-icons/lu";
import AuthLayout, { AuthField } from "../components/auth/AuthLayout";
import { useLanguage } from "../context/LanguageContext";
import "../styles/Auth.scss";
import { getProfile, login as loginUser, loginWithGoogle, storeAuthSession } from "../api/auth";
import useGoogleSignIn from "../hooks/useGoogleSignIn";

const LoginPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTimeoutRef = useRef(null);
  const prefillEmail = location.state?.emailPrefill ?? "";
  const registered = Boolean(location.state?.registered);
  const [form, setForm] = useState({
    email: prefillEmail,
    password: "",
    remember: false,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(
    registered
      ? t("auth.login.postRegisterSuccess", "Account created. Log in using your credentials.")
      : ""
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { requestCredential, isLoading: isGoogleLoading } = useGoogleSignIn();

  const finalizeLogin = useCallback(
    async (result, remember) => {
      await getProfile();
      storeAuthSession(result?.expiresUtc, remember);

      setSuccess(t("auth.login.success", "Welcome back! Redirecting..."));
      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
      redirectTimeoutRef.current = window.setTimeout(() => {
        navigate("/profile");
      }, 900);
    },
    [navigate, t]
  );

  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!form.email || !form.password) {
      setError(t("auth.login.error.required", "Please enter both email and password."));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await loginUser(form.email, form.password, form.remember);
      await finalizeLogin(result, form.remember);
    } catch (err) {
      setError(
        err?.message ?? t("auth.login.error.generic", "Invalid credentials. Please try again.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildPasswordToggleLabel = (isVisible) =>
    isVisible
      ? t("auth.password.hide", "Hide password")
      : t("auth.password.show", "Show password");

  const consumePendingGoogleToken = useCallback(async () => {
    const pendingToken = sessionStorage.getItem("wayli.google.oauth.token");
    if (!pendingToken) return;

    setError("");
    setSuccess("");
    setIsSubmitting(true);
    try {
      const result = await loginWithGoogle(pendingToken, true);
      await finalizeLogin(result, true);
      sessionStorage.removeItem("wayli.google.oauth.token");
      sessionStorage.removeItem("wayli.google.oauth.state");
      sessionStorage.removeItem("wayli.google.oauth.intent");
    } catch (err) {
      const message =
        err?.message ?? t("auth.login.error.google", "Could not sign in with Google right now.");
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [finalizeLogin, t]);

  useEffect(() => {
    consumePendingGoogleToken();
  }, [consumePendingGoogleToken]);

  const handleGoogleLogin = async () => {
    setError("");
    setSuccess("");
    setIsSubmitting(true);

    try {
      const credential = await requestCredential({ intent: "login" });
      const result = await loginWithGoogle(credential, form.remember);
      await finalizeLogin(result, form.remember);
    } catch (err) {
      const message =
        err?.message ?? t("auth.login.error.google", "Could not sign in with Google right now.");
      if (typeof message === "string" && message.toLowerCase().includes("cancel")) {
        return;
      }
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(
    () => () => {
      if (redirectTimeoutRef.current) {
        window.clearTimeout(redirectTimeoutRef.current);
      }
    },
    []
  );

  return (
    <AuthLayout
      eyebrow={t("auth.login.eyebrow", "Welcome back")}
      title={t("auth.login.title", "Log in to Wayli")}
      lead={t("auth.login.lead", "Pick up your next paddle plan right where you left off.")}
      illustration={
        <img
          src="/assets/pos_log.png"
          alt={t("auth.media.loginAlt", "Person preparing to paddle")}
          className="auth-illustration"
        />
      }
      footer={
        <p>
          {t("auth.login.footer.text", "Don't have an account?")}{" "}
          <Link to="/register">{t("auth.login.footer.link", "Create one")}</Link>
        </p>
      }
    >
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <AuthField
          id="login-email"
          label={t("auth.fields.email", "Email")}
          type="email"
          name="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={handleChange}
        />
        <AuthField
          id="login-password"
          label={t("auth.fields.password", "Password")}
          type={isPasswordVisible ? "text" : "password"}
          name="password"
          autoComplete="current-password"
          required
          value={form.password}
          onChange={handleChange}
          endAdornment={
            <button
              type="button"
              className="auth-field__password-toggle"
              onClick={() => setIsPasswordVisible((prev) => !prev)}
              aria-label={buildPasswordToggleLabel(isPasswordVisible)}
              aria-pressed={isPasswordVisible}
              title={buildPasswordToggleLabel(isPasswordVisible)}
            >
              {isPasswordVisible ? <LuEyeOff aria-hidden="true" /> : <LuEye aria-hidden="true" />}
            </button>
          }
        />

        <div className="auth-form__row">
          <label className="auth-checkbox">
            <input
              type="checkbox"
              name="remember"
              checked={form.remember}
              onChange={handleChange}
            />
            <span>{t("auth.login.rememberMe", "Remember me")}</span>
          </label>
          <Link to="/forgot-password" className="auth-link">
            {t("auth.login.forgotPassword", "Forgot password?")}
          </Link>
        </div>

        {(error || success) && (
          <div className={`auth-message${error ? " auth-message--error" : " auth-message--success"}`}>
            {error || success}
          </div>
        )}

        <button type="submit" className="auth-button auth-button--primary" disabled={isSubmitting}>
          {isSubmitting ? t("auth.login.primaryCtaLoading", "Logging in...") : t("auth.login.primaryCta", "Log in")}
        </button>
        <button
          type="button"
          className="auth-button auth-button--secondary"
          onClick={handleGoogleLogin}
          disabled={isSubmitting || isGoogleLoading}
        >
          <FcGoogle className="auth-button__icon" />
          {t("auth.login.googleCta", "Continue with Google")}
        </button>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
